/// <amd-module name="components/taskHandling/shared/modals/rejectionManagementWizard/emailAttachmentsDropzone"/>
import * as React from "react";
import {
    FileItem,
    FileItemProps,
    FlexLayout,
    FileUploader,
    FileUploaderProps,
    IconFileFileFill,
    SelectionErrorResponse,
    Notification,
    NotificationFeedbackTypeEnum,
} from "@medius/ui-controls";
import { getLabelTranslation } from "Core/Medius.Core.Web/Scripts/lib/globalization";

export type EmailAttachmentItem = FileItemProps & { file: File };

/**
 * Props for the generic EmailAttachmentsDropzone component.
 * Extends FileUploaderProps for dropzone configuration.
 */
export type EmailAttachmentsDropzoneProps = Omit<
    FileUploaderProps,
    "onAccepted" | "onRejected" | "isDropZone" | "maxSize" | "maxFiles"
> & {
    attachments: EmailAttachmentItem[];
    onAttachmentsChange: (attachments: EmailAttachmentItem[]) => void;
    maxSize: number;
    maxFiles: number;
};

const createFileKey = (file: File) => `${file.name}-${file.size}-${file.lastModified}`;

export const EmailAttachmentsDropzone = ({
    attachments,
    onAttachmentsChange,
    maxFiles,
    maxSize,
    disabled,
    ...uploaderProps
}: EmailAttachmentsDropzoneProps) => {
    const [errorMessages, setErrorMessages] = React.useState<string[]>([]);

    const existingKeys = new Set(attachments.map((a) => a.fileKey));

    const removeByKey = (fileKey: string) => {
        onAttachmentsChange(attachments.filter((f) => f.fileKey !== fileKey));
    };

    const handleAccepted = (acceptedFiles: File[]) => {
        setErrorMessages([]);
        const updatedAttachments = [
            ...attachments,
            ...acceptedFiles
                .filter((file) => !existingKeys.has(createFileKey(file)))
                .map((file) => ({
                    fileKey: createFileKey(file),
                    file,
                    name: file.name,
                    size: file.size
                } as EmailAttachmentItem))];


        const totalUpdatedAttachmentSize = updatedAttachments.reduce(
            (sum, attachment) => sum + (attachment.size || 0),
            0
        );

        if (totalUpdatedAttachmentSize > maxSize) {
            setErrorMessages([getLabelTranslation(
                "#PurchaseToPay/documentReasonCodes/rejectManagementWizard/fileTooLarge",
                String(maxSize / 1_000_000) + "MB"
            )]);
            return;
        }

        onAttachmentsChange(updatedAttachments);
    };

    const handleRejected = (response: SelectionErrorResponse) => {
        const fileErrors = (response?.fileErrors ?? [])
            .filter(({ file }) => !existingKeys.has(createFileKey(file)));

        const updatedErrorMessages = new Set<string>();

        for (const { file, errors = [] } of fileErrors) {
            if (errors.some(e => e.code === "fileTooLarge")) {
                updatedErrorMessages.add(
                    getLabelTranslation(
                        "#PurchaseToPay/documentReasonCodes/rejectManagementWizard/fileTooLarge",
                        `${maxSize / 1_000_000}MB`
                    )
                );
            }

            if (errors.some(e => e.code === "fileInvalidType")) {
                updatedErrorMessages.add(
                    getLabelTranslation(
                        "#PurchaseToPay/documentReasonCodes/rejectManagementWizard/invalidFileFormat",
                        file.name
                    )
                );
            }
        }

        setErrorMessages(_ => Array.from(updatedErrorMessages));
    }

    return (
        <FlexLayout direction="column" horizontalAlignment="fill">
            <FlexLayout direction="column" horizontalAlignment="fill">
                {attachments.map((file) => (
                    <FileItem
                        key={file.fileKey}
                        icon={file.icon ?? <IconFileFileFill />}
                        onDelete={disabled ? null : () => removeByKey(file.fileKey)}
                        onCancel={disabled ? null : () => removeByKey(file.fileKey)}
                        {...file}
                    />
                ))}
            </FlexLayout>

            <FileUploader
                maxFiles={maxFiles}
                isDropZone
                onAccepted={handleAccepted}
                onRejected={handleRejected}
                acceptDirectories={false}
                maxSize={maxSize}
                disabled={disabled}
                {...uploaderProps}
            />

            {errorMessages.map((message, index) => (
                <Notification
                    key={`${message}-${index}`}
                    feedbackType={NotificationFeedbackTypeEnum.Warning}
                    text={message}
                    onRequestClose={() => setErrorMessages((prev) => prev.filter((_, i) => i !== index))}
                />
            ))}
        </FlexLayout>
    );
};